<template>
  <div class="sd">
    <FiPageRail title="SOS Request Receiver Details" noFav nowrapActions>
      <template #actions>
        <b-button v-if="!standalone" size="sm" variant="outline-danger" :disabled="dismissing" @click="dismiss"
                  title="Close this request and remove it from your list" aria-label="Close request">
          <i class="fa fa-check"></i>&nbsp;Close Request
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="showInfo = true" title="About this page" aria-label="About this page">
          <i class="fa fa-info-circle"></i>
        </b-button>
      </template>
    </FiPageRail>

    <b-modal v-model="showInfo" title="About the SOS Map" ok-only ok-title="Close">
      <p>This map shows where the SOS vehicle is, along with nearby vehicles and yards that could respond.</p>
      <ul class="sd-info-list">
        <li><span class="sd-dot sd-dot--source"></span> The vehicle that raised the SOS</li>
        <li><span class="sd-dot sd-dot--internal"></span> Your nearby vehicles &mdash; a bus icon marked <b>V1, V2&hellip;</b></li>
        <li><span class="sd-dot sd-dot--yard"></span> Your / Fleet Int yards &mdash; a building icon marked <b>Y1, Y2&hellip;</b></li>
      </ul>
      <p class="sd-info-note">Each pin's number matches its row in the list, so you can tell which pin is which. Click any location in the list to center the map on it. The communication log at the bottom shows everything received from the driver, sent to dispatch, responded to the driver, and broadcast to other companies.</p>
    </b-modal>

    <div v-if="loading" class="sd-loading">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <template v-else>
      <!-- receiver view: the broadcast that reached this company. view-only; "Close Request" (header)
           removes this company's copy. The source company + operator contact are in the request info. -->
      <!-- split view: request info + communication log on the left, map + yards on the right -->
      <div class="sd-split">
        <div class="sd-pane-map">
      <div class="sd-body">
        <!-- request info at the very top of the main column -->
        <div class="sd-details">
          <div class="sd-detail"><span class="sd-detail-label">From</span><span class="sd-detail-value">{{ event.source_tenant_name || 'another company' }}</span></div>
          <div class="sd-detail"><span class="sd-detail-label">Requested</span><span class="sd-detail-value">{{ formatTime(event.processed_time) }}</span></div>
          <div class="sd-detail" v-if="event.operator_contact_name"><span class="sd-detail-label">Operator Contact</span><span class="sd-detail-value">{{ event.operator_contact_name }}</span></div>
          <div class="sd-detail" v-if="event.operator_contact_phone"><span class="sd-detail-label">Operator #</span><span class="sd-detail-value"><a :href="'tel:' + event.operator_contact_phone"><i class="fa fa-phone"></i>&nbsp;{{ event.operator_contact_phone }}</a></span></div>
          <div class="sd-detail" v-if="event.operator_contact_email"><span class="sd-detail-label">Operator Email</span><span class="sd-detail-value"><a :href="'mailto:' + event.operator_contact_email">{{ event.operator_contact_email }}</a></span></div>
          <div class="sd-detail sd-detail--wide"><span class="sd-detail-label">Location</span><span class="sd-detail-value" :title="sourceAddress">{{ sourceAddress || '—' }}</span></div>
          <div class="sd-detail sd-detail--wide" v-if="requestNotes"><span class="sd-detail-label">Notes</span><span class="sd-detail-value" :title="requestNotes">{{ requestNotes }}</span></div>
        </div>

        <!-- the SOS bus sits above the map -->
        <div class="sd-group sd-group--sosbus">
          <div class="sd-group-title">SOS bus</div>
          <button type="button" v-for="veh in sourceLocs" :key="veh.name"
                  class="sd-loc sd-loc--source" @click="focus_loc(veh.name)">{{ veh.name }}</button>
        </div>

        <div class="sd-side">
          <div class="sd-group" v-if="routeLocs.length">
            <div class="sd-group-title">Planned route</div>
            <button type="button" v-for="veh in routeLocs" :key="veh.name"
                    class="sd-loc sd-loc--route" @click="focus_loc(veh.name)">{{ veh.name }} location</button>
          </div>

          <div class="sd-group" v-if="internalLocs.length">
            <button type="button" class="sd-group-title sd-group-title--toggle" @click="toggleGroup('internal')" :aria-expanded="!!expandedGroups.internal">
              <i class="fa" :class="expandedGroups.internal ? 'fa-caret-down' : 'fa-caret-right'" aria-hidden="true"></i>&nbsp;your vehicles<span class="sd-group-count">{{ internalLocs.length }}</span>
            </button>
            <div v-for="veh in internalLocs" :key="veh.name" v-show="expandedGroups.internal" class="sd-loc-wrap">
              <button type="button" class="sd-loc sd-loc--internal" :class="{ 'sd-loc--selected': selectedVehicleName === veh.name }" @click="select_vehicle(veh)">
                <span class="sd-loc-num">V{{ vehicleNumbers[veh.name] }}</span>
                <span class="sd-loc-name">{{ veh.name }}</span>
                <span class="sd-loc-hint">{{ selectedVehicleName === veh.name ? 'Hide status' : 'Tap for live status' }}</span>
              </button>
              <sos-vehicle-context v-if="selectedVehicleName === veh.name"
                                   :vehicle="veh" :standalone="standalone" :token="standaloneToken || ''"
                                   :request-id="($route.query.request_id) || ''" :tenant="(user && user.tenant) || ''"
                                   @breadcrumb="onBreadcrumb"/>
            </div>
          </div>

          <!-- a partner only sees THEIR OWN yards (and vehicles above, if GPS-enabled); the source
               company's directory / network / public yards are the sender's "who can help" view -->
          <div class="sd-group" v-if="yourYards.length">
            <button type="button" class="sd-group-title sd-group-title--toggle" @click="toggleGroup('yours')" :aria-expanded="!!expandedGroups.yours">
              <i class="fa" :class="expandedGroups.yours ? 'fa-caret-down' : 'fa-caret-right'" aria-hidden="true"></i>&nbsp;your yards<span class="sd-group-count">{{ yourYards.length }}</span>
            </button>
            <div v-for="veh in yourYards" :key="veh.name" v-show="expandedGroups.yours" class="sd-loc-wrap">
              <button type="button" class="sd-loc sd-loc--yard" :class="{ 'sd-loc--selected': selectedYardName === veh.name }" @click="select_yard(veh)">
                <span class="sd-loc-num">Y{{ yardNum(veh.name) }}</span>
                <span class="sd-loc-name">{{ veh.name }}</span>
                <span v-if="veh.company && veh.company !== veh.name" class="sd-loc-company">{{ veh.company }}</span>
                <span v-if="veh.address" class="sd-loc-addr">{{ veh.address }}</span>
              </button>
              <div v-if="selectedYardName === veh.name" class="sd-loc-contact">
                <div v-if="veh.contact_name" class="sd-contact-line"><b>Contact:</b> {{ veh.contact_name }}</div>
                <a v-if="veh.phone" :href="'tel:' + veh.phone" class="sd-contact-phone"><i class="fa fa-phone"></i>&nbsp;{{ veh.phone }}</a>
                <div v-else class="sd-contact-none">No phone on file</div>
              </div>
            </div>
          </div>
        </div>

        <div class="sd-mapwrap">
          <gmap-map ref="gmap"
                    :zoom="summary.zoom || 9"
                    :center="center"
                    :options="themed_map_options"
                    class="sd-map">
            <gmap-marker
                v-for="(marker, idx) in markers"
                :key="idx"
                :position="marker.position"
                :title="marker.title"
                :icon="marker.icon"
                :label="marker.label"
                :clickable="true"
                @mouseover="openInfoWindowTemplate(idx)"
                @click="openInfoWindowTemplate(idx)"
                @mouseout="infoWindow.open = false"
            />
            <!-- the selected nearby vehicle's last-hour GPS trail -->
            <gmap-polyline v-if="breadcrumbPath.length" :path="breadcrumbPath" :options="breadcrumbOptions"/>
            <gmap-info-window
                :options="{ pixelOffset: { width: 0, height: -38 } }"
                :position="infoWindow.position"
                :opened="infoWindow.open">
              <div v-html="infoWindow.template"></div>
            </gmap-info-window>
          </gmap-map>
        </div>

          <!-- the sending company's itinerary (moved into the main column; read-only, shown only
               when the source dispatcher shared it) -->
          <div v-if="event.share_itinerary && itineraryLegs.length" class="sd-itin">
            <div class="sd-itin-head">
              <span class="sd-itin-title"><i class="fa fa-route"></i>&nbsp;Itinerary</span>
              <span v-if="itineraryMeta" class="sd-itin-meta">{{ itineraryMeta }}</span>
            </div>
            <div v-for="(leg, i) in itineraryLegs" :key="i" class="sd-itin-leg">
              <div v-if="leg.description" class="sd-itin-desc">{{ leg.description }}</div>
              <div v-for="(stop, j) in leg.stops" :key="j" class="sd-itin-stop">
                <span class="sd-itin-kind">{{ stop.kind }}</span>
                <span class="sd-itin-time">{{ formatTime(stop.time) }}</span>
                <span class="sd-itin-addr" :title="stop.address">{{ stop.address || '—' }}</span>
              </div>
            </div>
          </div>
      </div>
        </div><!-- /.sd-pane-map -->
        <!-- a partner receiver gets request info + map/yards only; the communication log and the
             reply thread are dispatch-only (they coordinate via the operator contact) -->
      </div><!-- /.sd-split -->
    </template>
  </div>
</template>
<script>
import { get_sos_details_authed, get_sos_detail_by_token, delete_sos_by_token } from '../networking/apis'
import { mapState } from "vuex";
import { DARK_MAP_STYLES } from '../assets/dark_map_styles'
import SosVehicleContext from './sos_vehicle_context.vue'

const MARKER_COLORS = {
  source: '#e0574b',
  begin: '#3aa393',
  arrival: '#e0916b',
  internal: '#5aa974',
  yard: '#5b9bd5',
}

// yard pins are colored by which list group they belong to, so a pin on the map reads
// as the same category as its row in the list (your yards / network / public directory)
const YARD_GROUP_COLORS = {
  fleetint: '#2f7fd6',  // your own / Fleet Int directory yards (blue)
  network: '#4b45b8',   // association-network yards (indigo)
  public: '#7a8794',    // public directory yards (slate)
}

export default {
  name: 'sos_receiver_detail',
  components: { SosVehicleContext },
  // the RECEIVER (partner) view of an SOS broadcast that reached this company. View-only: no
  // lifecycle / broadcast controls (only the sending company owns those). Reuses the sender page's
  // map, side yard-list and communication-log markup + CSS. Two data modes, same as the sender:
  //   authed:      signed-in staff, addressed by ?request_id (get_sos_details_authed)
  //   standalone:  login-free, addressed by the per-event share token (a `token` prop or the
  //                /sos/<slug>/<token>/dispatcher route param); `payload` seeds the first render.
  props: {
    // pre-fetched detail payload ({ summary, results, event, ... }); when present we render it
    // directly (standalone) instead of fetching
    payload: { type: Object, default: null },
    // the per-event share token for standalone comments (from the wrapper); falls back to the route
    token: { type: String, default: '' },
  },
  data() {
    return {
      items: [],
      sos_locations: [],
      internal_vehicles: [],
      nearby_yards: [],
      registered_yards: [],
      expandedGroups: {},   // the "your vehicles" / "your yards" lists are collapsed by default
      event: {},
      summary: {},
      loading: true,
      center: null,
      markers: [],
      standalone: true,
      // once we've rendered the wrapper-provided payload once, later reloads refetch by token so the
      // latest event state shows up
      seeded: false,
      dismissing: false,
      selectedYardName: null,
      // the nearby vehicle whose live "can this bus help?" panel is expanded, and its last-hour trail
      selectedVehicleName: null,
      vehicleBreadcrumb: [],
      // matches the fleet map basemap (vehicle_map.vue); midnight users get the dark variant
      mapStyle: {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: 'landscape', stylers: [{ color: '#F3F4F9' }] },
          { featureType: 'water', stylers: [{ color: '#C1CDE4' }] },
          { featureType: 'landscape.natural.terrain', stylers: [{ color: '#E0EDCA' }] },
          { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#DDDDDD' }] },
          { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#FFFFFF' }] },
          { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#FEF8D6' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        ],
      },
      showInfo: false,
      infoWindow: {
        position: {lat: 0, lng: 0},
        open: false,
        template: ''
      }
    }
  },
  async created() {
    this.load_data()
  },
  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    themed_map_options() {
      // follow the SAME signal the page's CSS tokens use — the data-fi-theme attribute on <html> —
      // so the map's light/dark can never diverge from the panels. (Reading site_theme too keeps
      // this computed reactive to an in-session theme toggle.) Only the dark "midnight" horizon
      // variant gets dark tiles; the light horizon ('new') and 'original' keep the light basemap.
      const storeTheme = (this.$store.state.profile || {}).site_theme
      const attrTheme = (typeof document !== 'undefined' && document.documentElement.getAttribute('data-fi-theme')) || storeTheme
      const midnight = attrTheme === 'midnight'
      return midnight ? { ...this.mapStyle, styles: DARK_MAP_STYLES } : this.mapStyle
    },
    sourceAddress() {
      const g = this.event && this.event.source_gps_info
      return (g && g.reverseGeo && g.reverseGeo.formattedLocation) || ''
    },
    driverName() {
      const d = this.event && this.event.source_driver_details
      if (!d) return ''
      if (typeof d === 'string') return d
      return d.DriverName || d.driver_name || d.name || ''
    },
    driverPhone() {
      const d = this.event && this.event.source_driver_details
      let p = ''
      if (d && typeof d === 'object') p = d.source_driver_phone || d.driver_phone || d.phone || ''
      // origination_number is the source company's callback — surfaced on received copies too, so a
      // relief vehicle can reach the stranded driver
      if (!p) p = (this.event && this.event.origination_number) || ''
      // only surface a real phone number, not a staff username used as the origin
      return /\d{3}/.test(p) ? p : ''
    },
    requestNotes() {
      const n = this.event && this.event.request_notes
      return (n && n !== 'None') ? n : ''
    },
    // the shared booking itinerary, normalized into ordered legs with their present stops
    // (Depart / Arrive / Return). Empty unless the backend sent it (it only does when the sending
    // dispatcher shared it), so the card's v-if hides cleanly otherwise.
    itineraryLegs() {
      const it = this.event && this.event.source_booking_itinerary
      const legs = it && Array.isArray(it.legs) ? it.legs : []
      return legs.map(l => {
        const stops = []
        if (l.begin_time || l.begin_address) stops.push({ kind: 'Depart', time: l.begin_time, address: l.begin_address })
        if (l.arrival_time || l.arrival_address) stops.push({ kind: 'Arrive', time: l.arrival_time, address: l.arrival_address })
        if (l.end_time || l.end_address) stops.push({ kind: 'Return', time: l.end_time, address: l.end_address })
        return { description: l.description || '', stops }
      }).filter(l => l.stops.length)
    },
    itineraryMeta() {
      const it = this.event && this.event.source_booking_itinerary
      if (!it) return ''
      const parts = []
      if (it.label) parts.push(it.label)
      if (it.booking_id) parts.push('#' + it.booking_id)
      return parts.join(' · ')
    },
    requestSubject() {
      // what the request is for: the vehicle name, or "pin" for a pin-drop SOS with no vehicle
      return (this.event && this.event.source_vehicle_name) || 'pin'
    },
    standaloneToken() {
      // the per-event share token for a login-free view: the `token` prop (from the wrapper), the
      // /sos/<slug>/<token>/dispatcher route param, or a ?token= query (the /sos_map link)
      return this.token || this.$route.params.token || this.$route.query.token || null
    },
    isAuthedMode() {
      // signed-in staff addressed by ?request_id, with no standalone token / payload in play
      return !this.payload && !this.standaloneToken &&
             !!(this.user && this.user.tenant && this.$route.query.request_id)
    },
    actionToken() {
      // token a comment is addressed by: event.share_token when authed, the standalone token otherwise
      return this.standalone
        ? this.standaloneToken
        : ((this.event && this.event.share_token) || null)
    },
    sourceLocs() {
      return (this.items || []).filter(i => i.type === 'source')
    },
    routeLocs() {
      return (this.items || []).filter(i => i.type === 'begin' || i.type === 'arrival')
    },
    internalLocs() {
      return (this.items || []).filter(i => i.type === 'internal')
    },
    // a partner sees only THEIR OWN nearby yards; a received (partner-copy) event carries no
    // directory / network / public yards (those are the source company's "who can help" view)
    yourYards() {
      return this.nearby_yards || []
    },
    // number the nearby vehicles (V1, V2…) in list order so each bus pin's label maps to its row
    vehicleNumbers() {
      const m = {}
      this.internalLocs.forEach((v, i) => { m[v.name] = i + 1 })
      return m
    },
    // number the receiver's own yards (Y1, Y2…) in list order so each pin's label maps to its row
    yardCatalog() {
      const m = {}
      let n = 0
      for (const y of this.yourYards) { m[y.name] = { num: ++n, group: 'fleetint' } }
      return m
    },
    breadcrumbPath() {
      return (this.vehicleBreadcrumb || [])
        .map(p => ({ lat: Number(p.latitude), lng: Number(p.longitude) }))
        .filter(p => !isNaN(p.lat) && !isNaN(p.lng))
    },
    breadcrumbOptions() {
      return { strokeColor: '#2f7d4f', strokeOpacity: 0.85, strokeWeight: 3, clickable: false, geodesic: true }
    },
  },
  methods: {
    formatTime(iso) {
      if (!iso) return '—'
      const dt = new Date(iso)
      if (isNaN(dt.getTime())) return iso
      return dt.toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    },
    focus_loc(selected_name) {
      const loc = (this.items || []).filter(i => i.name === selected_name)[0]
      if (loc != null) {
        this.center = { lat: Number(loc.latitude), lng: Number(loc.longitude) }
        if (this.$refs.gmap) {
          this.$refs.gmap.$mapPromise.then(map => { map.setZoom(12) })
        }
      }
    },
    // zoom/pan so every pin (source, vehicles, yards) is in view — the fixed zoom clipped
    // markers near the edge of the radius on small/mobile viewports
    fit_to_markers() {
      if (!this.$refs.gmap || !this.markers.length) return
      this.$refs.gmap.$mapPromise.then((map) => {
        const g = window.google && window.google.maps
        if (!g) return
        if (this.markers.length === 1) {
          map.setCenter(this.markers[0].position)
          map.setZoom(this.summary.zoom || 12)
          return
        }
        const bounds = new g.LatLngBounds()
        this.markers.forEach(m => bounds.extend(new g.LatLng(m.position.lat, m.position.lng)))
        map.fitBounds(bounds)
      }).catch(() => {})
    },
    toggleGroup(key) {
      this.$set(this.expandedGroups, key, !this.expandedGroups[key])
    },
    select_yard(veh) {
      if (this.selectedYardName === veh.name) {
        this.selectedYardName = null
        return
      }
      this.selectedYardName = veh.name
      this.focus_loc(veh.name)
    },
    select_vehicle(veh) {
      // toggle the live-status panel for a nearby vehicle; clear the map trail when collapsing
      if (this.selectedVehicleName === veh.name) {
        this.selectedVehicleName = null
        this.vehicleBreadcrumb = []
        return
      }
      this.selectedVehicleName = veh.name
      this.vehicleBreadcrumb = []
      this.focus_loc(veh.name)
    },
    onBreadcrumb(points) {
      // only honor the trail for the vehicle still selected (a slow response can arrive after a switch)
      this.vehicleBreadcrumb = Array.isArray(points) ? points : []
    },
    openInfoWindowTemplate(index) {
      const loc = this.items[index]
      this.infoWindow.position = { lat: Number(loc.latitude), lng: Number(loc.longitude) }
      this.infoWindow.template = loc.marker_html
      this.infoWindow.open = true
    },
    async load_data() {
      this.loading = true
      try {
        this.summary = {}
        this.items = []
        this.markers = []

        // signed-in staff get the full event from the authed, tenant-scoped endpoint; a login-free
        // visitor gets the same rich payload (event public-stripped) from the token endpoint. When
        // the sos_dispatch wrapper handed us the payload we seed from it, but still refresh from the
        // token so a new comment shows up after posting.
        let data = {}
        if (this.isAuthedMode) {
          this.standalone = false
          const {value: { data: d = {} } } = await get_sos_details_authed(this.user.tenant, this.$route.query.request_id)
          data = d
        } else {
          this.standalone = true
          if (this.payload && !this.seeded) {
            // first render from the wrapper-provided payload — no fetch
            this.seeded = true
            data = this.payload
          } else if (this.standaloneToken) {
            const {value: { data: d = {} } } = await get_sos_detail_by_token(this.standaloneToken)
            data = d
          } else if (this.payload) {
            data = this.payload
          }
        }

        this.summary = data.summary || {}
        this.items = data.results || []
        this.event = data.event || {}
        this.sos_locations = data.sos_locations || []
        this.internal_vehicles = data.internal_vehicles || []
        this.nearby_yards = data.nearby_yards || []
        this.registered_yards = data.registered_yards || []

        this.markers = []
        for (const loc of this.items) {
          const position = { lat: Number(loc.latitude), lng: Number(loc.longitude) }
          if (loc.type === 'source') this.center = position
          let color = MARKER_COLORS[loc.type] || MARKER_COLORS.yard
          let label = null
          if (loc.type === 'internal') {
            // vehicles: number the bus pin (V1, V2…) to match its row in the nearby-vehicles list
            const n = this.vehicleNumbers[loc.name]
            if (n) label = this.markerLabel('V' + n)
          } else if (loc.type === 'yard') {
            // yards: color the building pin by its list group and number it (Y1, Y2…) to match its row
            const meta = this.yardCatalog[loc.name]
            if (meta) { color = YARD_GROUP_COLORS[meta.group] || color; label = this.markerLabel('Y' + meta.num) }
          }
          this.markers.push({ position, title: loc.name, icon: this.markerIcon(loc.type, color), type: loc.type, label })
        }
      } catch (e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
      this.$nextTick(this.fit_to_markers)
    },
    async dismiss() {
      // remove THIS company's copy of the SOS from their list; the sending company (and every other
      // partner) still keeps theirs, and the shared comment thread on the source is untouched
      if (this.dismissing) return
      if (!window.confirm('Remove this SOS from your list? The sending company still has it.')) return
      this.dismissing = true
      try {
        await delete_sos_by_token(this.actionToken)
        this.$emit('showAlert', 'removed from your list', 'success', 3000)
        this.$router.push({ name: 'sos_received_list' })
      } catch (e) {
        this.$emit('showAlert', 'could not remove this SOS', 'danger', 4000)
        this.dismissing = false
      }
    },
    pinSymbol(color) {
      // square markers, consistent with the SOS overlay map
      return {
        path: 'M -3,-3 L 3,-3 L 3,3 L -3,3 Z',
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 0.6,
        scale: 3.4,
      }
    },
    busSymbol(color, scale = 1.3) {
      // a small bus silhouette (body + two wheels) so nearby vehicles read clearly as buses
      return {
        path: 'M -10,-5 L 10,-5 L 10,3 L 7,3 L 7,5 L 4,5 L 4,3 L -4,3 L -4,5 L -7,5 L -7,3 L -10,3 Z',
        fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 0.8, scale,
      }
    },
    yardSymbol(color) {
      // a small building/depot silhouette so yards read clearly as yards
      return {
        path: 'M -8,0 L 0,-7 L 8,0 L 5.5,0 L 5.5,6 L -5.5,6 L -5.5,0 Z',
        fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 0.8, scale: 1.4,
      }
    },
    markerIcon(type, color) {
      if (type === 'source') return this.busSymbol(color, 1.4)  // the SOS bus itself -> a red bus, slightly larger than responders
      if (type === 'internal') return this.busSymbol(color)    // nearby responder vehicles -> bus
      if (type === 'yard') return this.yardSymbol(color)       // yards -> building
      return this.pinSymbol(color)                             // itinerary stops -> square
    },
    // white number centered on the pin, matching the badge on the matching list row
    markerLabel(text) {
      return { text: String(text), color: '#ffffff', fontSize: '10px', fontWeight: '700' }
    },
    yardNum(name) {
      const meta = this.yardCatalog[name]
      return meta ? meta.num : ''
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      }
    }
  }
}
</script>
<style scoped>
.sd {
  padding: 16px;
  max-width: 100%;
  overflow-x: hidden;   /* never let the page scroll sideways on narrow screens */
}
.sd-details {
  display: flex;
  flex-wrap: wrap;
  gap: 3px 20px;
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-radius: 10px;
  padding: 8px 14px;
  margin-bottom: 12px;
}
.sd-detail {
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
  max-width: 100%;
}
.sd-detail--wide .sd-detail-value {
  /* SOS notes always show in full: wrap across lines rather than truncating to one */
  max-width: 100%;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
}
.sd-detail-label {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
  white-space: nowrap;
}
.sd-detail-value {
  font-size: 15px;
  color: var(--fi-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 340px;
}
.sd-detail-value a {
  color: var(--fi-link);
  text-decoration: none;
}
.sd-detail-value a:hover {
  text-decoration: underline;
}
.sd-itin {
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
}
.sd-itin-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.sd-itin-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
}
.sd-itin-meta {
  font-size: 13px;
  color: var(--fi-text-muted);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sd-itin-leg {
  padding-top: 6px;
  margin-top: 6px;
  border-top: 1px solid var(--fi-line);
}
.sd-itin-leg:first-of-type {
  border-top: 0;
  margin-top: 0;
  padding-top: 0;
}
.sd-itin-desc {
  font-size: 13px;
  font-weight: 600;
  color: var(--fi-text);
  margin-bottom: 2px;
}
.sd-itin-stop {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 14px;
  line-height: 1.5;
}
.sd-itin-kind {
  flex: 0 0 54px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
}
.sd-itin-time {
  flex: 0 0 auto;
  color: var(--fi-text);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.sd-itin-addr {
  color: var(--fi-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.sd-info-list {
  list-style: none;
  padding: 0;
  margin: 8px 0;
}
.sd-info-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.sd-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex: 0 0 auto;
}
.sd-dot--source { background: #e0574b; }
.sd-dot--internal { background: #5aa974; }
.sd-dot--yard { background: #2f7fd6; }
.sd-dot--network { background: #4b45b8; }
.sd-dot--public { background: #7a8794; }
.sd-info-note {
  color: var(--fi-text-faint);
  font-size: 14px;
}
.sd-loading {
  text-align: center;
  padding: 40px;
}
.sd-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}
.sd-pill {
  font-size: 13px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 999px;
}
.sd-pill--sent { background: color-mix(in srgb, #2b5aa6 12%, var(--fi-surface)); color: color-mix(in srgb, #2b5aa6 55%, var(--fi-text)); }
.sd-pill--received { background: color-mix(in srgb, #5a3fa6 12%, var(--fi-surface)); color: color-mix(in srgb, #5a3fa6 55%, var(--fi-text)); }
.sd-pill--source { background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text)); }
.sd-pill--internal { background: color-mix(in srgb, var(--fi-success) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text)); }
.sd-pill--yard { background: color-mix(in srgb, #23608f 12%, var(--fi-surface)); color: color-mix(in srgb, #23608f 55%, var(--fi-text)); }
.sd-pill--broadcast { background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text)); }
.sd-pill--warn { background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text)); }

.sd-responses {
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
}
.sd-resp-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.sd-resp-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
}
.sd-resp-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.sd-resp-escalate { margin-left: auto; }
.sd-resp-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sd-resp-item {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;
  font-size: 13px;
  padding: 4px 0;
  border-top: 1px solid var(--fi-line);
}
.sd-resp-status {
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--fi-surface-sunken);
  color: var(--fi-text-muted);
}
.sd-resp-status--en_route, .sd-resp-status--acknowledged, .sd-resp-status--arrived { background: color-mix(in srgb, var(--fi-success) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text)); }
.sd-resp-status--declined, .sd-resp-status--unavailable { background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text)); }
.sd-resp-who { color: var(--fi-text); font-weight: 600; }
.sd-resp-note { color: var(--fi-text-muted); }
.sd-resp-time { margin-left: auto; color: var(--fi-text-faint); font-size: 12px; }
.sd-resp-empty { font-size: 13px; color: var(--fi-text-faint); }

.sd-bcast-edit {
  margin-top: 10px;
  padding: 10px 12px;
  background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface));
  border: 1px solid color-mix(in srgb, var(--fi-warning-accent) 35%, var(--fi-line));
  border-radius: 10px;
}
.sd-bcast-edit-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
  margin-bottom: 8px;
}
.sd-bcast-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
}
.sd-bcast-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1 1 180px;
  min-width: 0;
  margin-bottom: 0;
}
.sd-bcast-field--wide { flex-basis: 100%; }
.sd-bcast-field > span {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--fi-text-faint);
}
.sd-bcast-edit-actions { display: flex; gap: 8px; margin-top: 10px; }
.sd-bcast-history {
  margin-top: 10px;
  padding: 8px 12px;
  background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface));
  border-radius: 8px;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sd-bcast-history-title { font-weight: 700; color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text)); }
.sd-bcast-entry {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 6px;
  border-top: 1px solid color-mix(in srgb, var(--fi-warning-accent) 35%, var(--fi-line));
}
.sd-bcast-entry:first-of-type { border-top: none; padding-top: 0; }
.sd-bcast-entry-head { display: flex; align-items: center; gap: 8px; }
.sd-bcast-kind {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  border-radius: 999px;
  padding: 1px 9px;
}
.sd-bcast-kind--send { background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text)); }
.sd-bcast-kind--update { background: color-mix(in srgb, #23608f 12%, var(--fi-surface)); color: color-mix(in srgb, #23608f 55%, var(--fi-text)); }
.sd-bcast-when { color: var(--fi-text-faint); font-size: 12px; }
.sd-bcast-msg { color: var(--fi-text); }
.sd-bcast-recips { display: flex; flex-wrap: wrap; gap: 6px; }
.sd-bcast-recip {
  background: color-mix(in srgb, var(--fi-warning) 12%, var(--fi-surface));
  color: color-mix(in srgb, var(--fi-warning) 62%, var(--fi-text));
  border-radius: 999px;
  padding: 2px 10px;
  font-weight: 600;
}
.sd-bcast-none { color: var(--fi-text-faint); }
.sd-rebcast {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sd-rebcast-label { font-size: 13px; font-weight: 600; color: var(--fi-text-muted); margin: 0; }
.sd-rebcast-send { align-self: flex-start; font-weight: 600; }
.sd-resp-hint {
  font-size: 13px;
  color: var(--fi-text-faint);
  background: var(--fi-surface-sunken);
  border-radius: 8px;
  padding: 8px 12px;
}

/* split page: main column (left, 75%: request info, SOS bus, map, then everything else)
   | communication log (narrow right column, 25%) */
.sd-split {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.sd-pane-map { flex: 1 1 100%; min-width: 0; order: 1; }   /* the only column now (comms log removed) */
/* the main column stacks: request info, SOS bus, map, then the vehicle/yard lists + itinerary */
.sd-pane-map .sd-body {
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}
.sd-pane-map .sd-details { order: -4; width: 100%; }
.sd-pane-map .sd-group--sosbus { order: -3; width: 100%; margin-bottom: 0; }
.sd-pane-map .sd-mapwrap { order: -2; width: 100%; }
.sd-pane-map .sd-side { order: -1; }
.sd-pane-map .sd-itin { order: 0; width: 100%; }
.sd-pane-map .sd-map { height: 30vh; min-height: 200px; }
.sd-pane-map .sd-side {
  flex: 1 1 auto;
  width: 100%;
  max-height: 72vh;
  min-height: 0;
}
.sd-body {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}
.sd-side {
  flex: 0 0 240px;
  /* scroll pane the height of the map, so a long yard list doesn't stretch the page */
  max-height: 76vh;
  min-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.sd-group {
  background: var(--fi-surface-sunken);
  border: 1px solid var(--fi-line);
  border-radius: 8px;
  padding: 8px 10px;
}
.sd-group-title {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--fi-text-faint);
  margin-bottom: 4px;
}
/* count sits at the right edge of the group header */
.sd-group-count { margin-left: auto; padding-left: 8px; font-variant-numeric: tabular-nums; }
/* collapsible group header (button reset over the flex .sd-group-title) */
.sd-group-title--toggle {
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.sd-group-title--toggle:hover { color: var(--fi-link); }
.sd-group-title--toggle .fa { font-size: 11px; width: 10px; flex: none; }
.sd-loc {
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.sd-loc:hover {
  filter: brightness(0.97);
}
.sd-loc--selected {
  outline: 2px solid var(--fi-rail-accent);
  outline-offset: -2px;
}
.sd-loc-wrap {
  margin-bottom: 6px;
}
.sd-loc-wrap .sd-loc {
  margin-bottom: 0;
}
.sd-loc-contact {
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: var(--fi-text-muted);
}
.sd-contact-line {
  margin-bottom: 4px;
}
.sd-contact-phone {
  display: inline-block;
  font-weight: 600;
  color: var(--fi-link);
  text-decoration: none;
}
.sd-contact-phone:hover {
  text-decoration: underline;
}
.sd-contact-none {
  color: var(--fi-text-faint);
}
.sd-loc--source { background: color-mix(in srgb, var(--fi-danger) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-danger) 55%, var(--fi-text)); }
.sd-loc--route { background: color-mix(in srgb, #2a8a7a 12%, var(--fi-surface)); color: color-mix(in srgb, #2a8a7a 55%, var(--fi-text)); }
.sd-loc--internal { background: color-mix(in srgb, var(--fi-success) 12%, var(--fi-surface)); color: color-mix(in srgb, var(--fi-success) 55%, var(--fi-text)); }
.sd-loc--network { background: color-mix(in srgb, #4b45b8 12%, var(--fi-surface)); color: color-mix(in srgb, #3b4aa0 55%, var(--fi-text)); }
.sd-loc--yard { background: color-mix(in srgb, #23608f 12%, var(--fi-surface)); color: color-mix(in srgb, #23608f 55%, var(--fi-text)); }
.sd-loc--public { background: var(--fi-surface-sunken); color: var(--fi-text-muted); }
.sd-loc-num {
  float: left;
  margin-right: 8px;
  min-width: 24px;
  padding: 1px 5px;
  border-radius: 6px;
  background: color-mix(in srgb, currentColor 15%, transparent);
  color: inherit;
  font-size: 11px;
  font-weight: 800;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.sd-loc-name { display: block; }
.sd-loc-hint {
  display: block;
  font-weight: 600;
  font-size: 11px;
  color: var(--fi-text-faint);
  margin-top: 2px;
}
.sd-loc--internal.sd-loc--selected { outline-color: var(--fi-success); }
.sd-loc-company {
  display: block;
  font-weight: 600;
  font-size: 12px;
  color: var(--fi-text-muted);
  margin-top: 1px;
}
.sd-loc-addr {
  display: block;
  font-weight: 400;
  font-size: 12px;
  color: var(--fi-text-faint);
  margin-top: 2px;
}
.sd-mapwrap {
  flex: 1 1 auto;
  min-width: 0;
}
.sd-map {
  width: 100%;
  height: 38vh;
  min-height: 240px;
  border-radius: 10px;
  overflow: hidden;
}

.sd-topbar {
  margin-bottom: 12px;
}
.sd-toprow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  flex-wrap: wrap;
}
.sd-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--fi-text);
  line-height: 1.25;
  min-width: 0;
  word-break: break-word;
}
.sd-topactions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}
.sd-topbar .sd-bcast-edit { margin-top: 10px; }
/* rebroadcast panel now sits below the communication log in the right column */
.sd-rebcast--panel {
  margin-top: 16px;
  background: var(--fi-surface);
  border: 1px solid var(--fi-line);
  border-radius: 12px;
  padding: 14px 16px;
}
@media (max-width: 768px) {
  .sd {
    padding: 10px;
  }
  .sd-details {
    gap: 3px 14px;
    padding: 8px 12px;
  }
  .sd-detail-value,
  .sd-detail--wide .sd-detail-value {
    max-width: 60vw;
  }
  .sd-title {
    font-size: 18px;
  }
  /* icon-only lifecycle buttons on mobile to save width */
  .sd-topactions .sd-btn-label {
    display: none;
  }
  /* the split stacks with the communication log on top, then map + yards
     (the panes keep their desktop order: log = 1, map = 2) */
  .sd-split {
    flex-direction: column;
    gap: 12px;
  }
  .sd-pane-map {
    flex: 1 1 auto;
    width: 100%;
  }
  .sd-body {
    flex-direction: column;
    align-items: stretch;   /* fill width in column mode (flex-start left the map sized to content) */
    gap: 10px;
  }
  .sd-mapwrap {
    order: -1;   /* map first on mobile */
    width: 100%;
  }
  .sd-side {
    flex: 1 1 auto;
    max-height: 240px;
    width: 100%;
  }
  .sd-map {
    width: 100%;
    max-width: 100%;
    height: 34vh;
    min-height: 200px;
  }
}
</style>
